import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  SelectChangeEvent,
  Tabs,
  Tab
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Rule, LoanApplication } from '../types';
import { addRule, updateRule, deleteRule, addSmeRule, updateSmeRule, deleteSmeRule } from '../store/configSlice';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from '../i18n/useTranslation';

const ruleFields: (keyof LoanApplication)[] = [
    'monthlyIncome', 'requestedAmount', 'employmentDuration', 'age', 'existingLoans', 'yearsInBusiness'
];
const operators: Rule['operator'][] = ['>=', '<=', '>', '<', '==='];

const RulesManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { rules, smeRules } = useAppSelector((state) => state.config);
  const [currentTab, setCurrentTab] = useState<'individual' | 'sme'>('individual');

  // Fetch rules data when component mounts
  useEffect(() => {
    // Import the fetch functions dynamically to avoid circular dependencies
    import('../store/configSlice').then(({ fetchRules, fetchSmeRules }) => {
      dispatch(fetchRules());
      dispatch(fetchSmeRules());
    });
  }, [dispatch]);

  const activeRules = currentTab === 'individual' ? rules : smeRules;
  const addAction = currentTab === 'individual' ? addRule : addSmeRule;
  const updateAction = currentTab === 'individual' ? updateRule : updateSmeRule;
  const deleteAction = currentTab === 'individual' ? deleteRule : deleteSmeRule;

  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [newRule, setNewRule] = useState<Partial<Rule>>({
    name: '',
    description: '',
    field: 'monthlyIncome',
    operator: '>=',
    value: '',
    score: 0,
  });

  const handleFieldChange = (event: SelectChangeEvent<any>) => {
    setNewRule({ ...newRule, field: event.target.value as Rule['field'] });
  };
  
  const handleOperatorChange = (event: SelectChangeEvent<any>) => {
    setNewRule({ ...newRule, operator: event.target.value as Rule['operator'] });
  };

  const resetForm = () => {
    setEditingRule(null);
    setNewRule({
      name: '', description: '', field: 'monthlyIncome',
      operator: '>=', value: '', score: 0
    });
  }

  const handleAddOrUpdateRule = () => {
    if (newRule.name && newRule.description && newRule.field && newRule.operator && newRule.value !== undefined) {
      const ruleData = {
        ...newRule,
        value: Number(newRule.value) || newRule.value,
        score: Number(newRule.score) || 0,
      };

      if (editingRule) {
        const updatedRule: Rule = { ...editingRule, ...ruleData };
        dispatch(updateAction(updatedRule));
      } else {
        const ruleToAdd: Rule = {
          id: uuidv4(),
          name: newRule.name!,
          description: newRule.description!,
          field: newRule.field!,
          operator: newRule.operator!,
          value: ruleData.value,
          score: ruleData.score,
        };
        dispatch(addAction(ruleToAdd));
      }
      resetForm();
    }
  };

  const handleEditRule = (rule: Rule) => {
    setEditingRule(rule);
    setNewRule(rule);
  };

  const handleDeleteRule = (id: string) => {
    dispatch(deleteAction(id));
  };

  return (
    <Box sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
      <Typography variant="h4" gutterBottom>{t('rules.title')}</Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={currentTab} onChange={(e, newValue) => {
            setCurrentTab(newValue);
            resetForm();
        }} aria-label="rules type tabs">
          <Tab label={t('rules.individualRules')} value="individual" />
          <Tab label={t('rules.smeRules')} value="sme" />
        </Tabs>
      </Box>
      
      <Box sx={{ p: { xs: 1, sm: 2, md: 3} }}>
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>{editingRule ? t('rules.edit') + ' ' + t('rules.ruleName') : t('rules.addRule')}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField 
                sx={{ flex: '1 1 300px', minWidth: '250px' }}
                label={t('rules.ruleName')} 
                value={newRule.name} 
                onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
              />
              <TextField 
                sx={{ flex: '1 1 300px', minWidth: '250px' }}
                label={t('rules.description')} 
                value={newRule.description} 
                onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: '1 1 200px', minWidth: '180px' }}>
                <InputLabel>{t('rules.field')}</InputLabel>
                <Select value={newRule.field} label="Field" onChange={handleFieldChange}>
                  {ruleFields.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                </Select>
              </FormControl>
              
              <FormControl sx={{ flex: '1 1 200px', minWidth: '180px' }}>
                <InputLabel>{t('rules.operator')}</InputLabel>
                <Select value={newRule.operator} label="Operator" onChange={handleOperatorChange}>
                  {operators.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
              
              <TextField 
                sx={{ flex: '1 1 200px', minWidth: '180px' }}
                label={t('rules.value')} 
                value={newRule.value} 
                onChange={(e) => setNewRule({ ...newRule, value: e.target.value })}
              />
              
              <TextField 
                sx={{ flex: '1 1 200px', minWidth: '180px' }}
                label={t('rules.score')} 
                type="number" 
                value={newRule.score} 
                onChange={(e) => setNewRule({ ...newRule, score: Number(e.target.value) })}
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button variant="contained" onClick={handleAddOrUpdateRule}>
                {editingRule ? t('rules.save') : t('rules.addRule')}
              </Button>
              {editingRule && (
                <Button variant="text" onClick={resetForm}>
                  {t('rules.cancel')}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>

        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead><TableRow><TableCell>{t('rules.ruleName')}</TableCell><TableCell>{t('rules.description')}</TableCell><TableCell>{t('rules.field')}</TableCell><TableCell>{t('rules.operator')}</TableCell><TableCell>{t('rules.value')}</TableCell><TableCell>{t('rules.score')}</TableCell><TableCell>{t('rules.actions')}</TableCell></TableRow></TableHead>
            <TableBody>
              {activeRules.map((rule) => (
                <TableRow key={rule.id} hover>
                  <TableCell>{rule.name}</TableCell><TableCell>{rule.description}</TableCell><TableCell>{rule.field}</TableCell><TableCell>{rule.operator}</TableCell><TableCell>{rule.value}</TableCell><TableCell>{rule.score}</TableCell>
                  <TableCell><IconButton onClick={() => handleEditRule(rule)}><EditIcon /></IconButton><IconButton onClick={() => handleDeleteRule(rule.id)}><DeleteIcon /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default RulesManagement; 